import { ChangeEvent, useEffect, useRef, useState } from "react";
import {
	Box,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import { useMsal } from "@azure/msal-react";
import { useAlertContext, useClinicContext, useUserContext } from "../contexts";
import {
	apiAccessRequest,
	SETTING_WAIT_TIME_NUMBER_GROUPS
} from "../constants";
import { BuildUrl } from "../helpers";
import { useCreateAuditLog, useHttp } from "../hooks";
import {
	AlertType,
	ApplicationScreen,
	Clinic,
	ClinicSetting,
	WaitTimeGroup
} from "../models";
import {
	ClinicSettingsSection,
	ClinicSettingsTabKey,
	ClinicSettingsTabs
} from "../components/ClinicSettings";

export const ClinicSettings = () => {
	const { user, account } = useUserContext();
	const { instance } = useMsal();
	const { showAlert, clearAlert } = useAlertContext();
	const { userClinics } = useClinicContext();
	const { createAuditLog } = useCreateAuditLog();

	const [clinic, setClinic] = useState<Clinic | undefined>(undefined);
	const [activeTab, setActiveTab] = useState<ClinicSettingsTabKey>("clinic");
	const [clinicSettings, setClinicSettings] = useState<
		ClinicSetting[] | undefined
	>(undefined);
	const [omSettings, setOMSettings] = useState<ClinicSetting[] | undefined>(
		undefined
	);
	const [doctorSettings, setDoctorSettings] = useState<
		ClinicSetting[] | undefined
	>(undefined);
	const [saSettings, setSASettings] = useState<ClinicSetting[] | undefined>(
		undefined
	);
	const [dataChanged, setDataChanged] = useState(false);
	const [errors, setErrors] = useState<string[] | undefined>(undefined);

	const originalClinicSettings = useRef<ClinicSetting[] | undefined>(undefined);
	const originalOmSettings = useRef<ClinicSetting[] | undefined>(undefined);
	const originalDoctorSettings = useRef<ClinicSetting[] | undefined>(undefined);
	const originalSASettings = useRef<ClinicSetting[] | undefined>(undefined);

	const clinicSettingsUrl = (clinicId: string) =>
		BuildUrl(`Clinic/ClinicSettings?id=${clinicId}`);
	const {
		data,
		isError,
		isLoading,
		fetchData: fecthClinicSettings
	} = useHttp<ClinicSetting[]>(clinicSettingsUrl(clinic?.id.toString() || ""), {
		method: "GET"
	});

	const updatedSettings = (
		nextOmSettings: ClinicSetting[] | undefined,
		nextDoctorSettings: ClinicSetting[] | undefined,
		nextClinicSettings: ClinicSetting[] | undefined,
		nextSASettings: ClinicSetting[] | undefined
	) =>
		(nextOmSettings || [])
			.concat(nextDoctorSettings || [])
			.concat(nextClinicSettings || [])
			.concat(nextSASettings || [])
			.map((setting) => ({
				id: setting.id,
				clinicId: setting.clinicId,
				settingId: setting.settingId,
				value: setting.value,
				username: user?.username
			}));

	useEffect(() => {
		if (userClinics && userClinics.length > 0) {
			setClinic(userClinics[0]);
		}
	}, [userClinics]);

	useEffect(() => {
		if (clinic) {
			fecthClinicSettings();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [clinic]);

	useEffect(() => {
		const excludeSettings = [
			"OM_DisplayDoctorsWorking",
			"OM_DisplayDoctorWaitTimes",
			"OM_WaitTimeCalculationMethod",
			"OM_WaitTimeNumberGroups",
			"OM_WaitTimePercentGroups",
			"DocCheckin_DisplayMovePatient",
			"DocCheckin_DisplayPatientsSeen",
			"DocCheckin_DisplayPatientsWaiting",
			"DocCheckin_DisplayRequestType",
			"DocCheckin_DisplayRequeuePatientForNonTelehealth",
			"DocCheckin_DisplayRequeuePatientForTelehealth"
		];

		if (!data) {
			return;
		}

		const nextClinicSettings = data.filter(
			(setting) =>
				!setting.settingName.startsWith("DocCheckin_") &&
				!setting.settingName.startsWith("OM_") &&
				!setting.settingName.startsWith("SA_")
		);
		const nextOmSettings = data.filter(
			(setting) =>
				setting.settingName.startsWith("OM_") &&
				!excludeSettings.includes(setting.settingName)
		);
		const nextDoctorSettings = data.filter(
			(setting) =>
				setting.settingName.startsWith("DocCheckin_") &&
				!excludeSettings.includes(setting.settingName)
		);
		const nextSASettings = data.filter((setting) =>
			setting.settingName.startsWith("SA_")
		);

		setClinicSettings(nextClinicSettings);
		setOMSettings(nextOmSettings);
		setDoctorSettings(nextDoctorSettings);
		setSASettings(nextSASettings);
		originalClinicSettings.current = cloneDeep(nextClinicSettings);
		originalOmSettings.current = cloneDeep(nextOmSettings);
		originalDoctorSettings.current = cloneDeep(nextDoctorSettings);
		originalSASettings.current = cloneDeep(nextSASettings);
		setDataChanged(false);
		setErrors(undefined);
	}, [data]);

	useEffect(() => {
		if (isError) {
			setAlert("Error fetching clinic settings", AlertType.error);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isError]);

	const handleClinicChange = async (event: SelectChangeEvent) => {
		const clinicId = event.target.value;
		setClinic(
			userClinics?.find((userClinic) => userClinic.id === Number(clinicId))
		);
	};

	const handleSettingChange = async (event: ChangeEvent<HTMLInputElement>) => {
		const { name, value, checked } = event.target;

		const getSettingsAndSetter = () => {
			if (name.startsWith("OM_")) {
				return { settings: omSettings, setSettings: setOMSettings };
			}

			if (name.startsWith("DocCheckin_")) {
				return { settings: doctorSettings, setSettings: setDoctorSettings };
			}

			if (name.startsWith("SA_")) {
				return { settings: saSettings, setSettings: setSASettings };
			}

			return { settings: clinicSettings, setSettings: setClinicSettings };
		};

		const updateSettingValue = (setting: ClinicSetting) => {
			if (setting.dataType === "boolean") {
				return { ...setting, value: checked ? "1" : "0" };
			}

			return { ...setting, value };
		};

		const { settings, setSettings } = getSettingsAndSetter();
		if (!settings) {
			return;
		}

		const settingIndex = settings.findIndex(
			(setting) => setting.settingName === name
		);
		if (settingIndex === -1) {
			return;
		}

		const updatedSetting = updateSettingValue(settings[settingIndex]);
		const nextSettings = [...settings];
		nextSettings.splice(settingIndex, 1, updatedSetting);
		setSettings(nextSettings);
		setDataChanged(true);
	};

	const handleWaitTimeGroupChange = async (
		event: ChangeEvent<HTMLInputElement>
	) => {
		const { name, value } = event.target;
		const [groupName, rangeKey] = name.split("-");
		const settingIndex = omSettings?.findIndex(
			(setting) => setting.settingName === SETTING_WAIT_TIME_NUMBER_GROUPS
		);

		if (omSettings == null || settingIndex == null || settingIndex === -1) {
			return;
		}

		const waitTimeGroups: WaitTimeGroup[] = JSON.parse(
			omSettings[settingIndex].value
		);
		const groupIndices: Record<string, number> = {
			short: 0,
			medium: 1,
			long: 2
		};
		const groupIndex = groupIndices[groupName];

		if (groupIndex === undefined) {
			return;
		}

		if (rangeKey === "min") {
			waitTimeGroups[groupIndex].min = Number(value);
		} else {
			waitTimeGroups[groupIndex].max = Number(value);
		}

		const nextOmSettings = [...omSettings];
		nextOmSettings.splice(settingIndex, 1, {
			...omSettings[settingIndex],
			value: JSON.stringify(waitTimeGroups)
		});
		setOMSettings(nextOmSettings);
		setDataChanged(true);
	};

	const setAlert = (message: string, alertType: AlertType) => {
		showAlert({
			message,
			alertType,
			timeout: true
		});

		setTimeout(() => {
			window.scrollTo(0, 0);
		}, 500);
	};

	const formatSettingName = (value: string) => {
		if (value.startsWith("OM_")) {
			return value.replace(/OM_/g, "Our Medical app ");
		}

		if (value.startsWith("DocCheckin_")) {
			return value.replace(/DocCheckin_/g, "Doctor's app ");
		}

		return value;
	};

	const callCreateAuditLog = async () => {
		try {
			const fromValue = (originalOmSettings.current || [])
				.concat(originalDoctorSettings.current || [])
				.concat(originalClinicSettings.current || [])
				.concat(originalSASettings.current || [])
				.map((setting) => ({
					settingName: setting.settingName,
					settingDescription: setting.settingDescription,
					value: setting.value
				}));

			const toValue = (omSettings || [])
				.concat(doctorSettings || [])
				.concat(clinicSettings || [])
				.concat(saSettings || [])
				.map((setting) => ({
					settingName: setting.settingName,
					settingDescription: setting.settingDescription,
					value: setting.value
				}));

			const differences = fromValue
				.filter((fromSetting) =>
					toValue.some(
						(toSetting) =>
							fromSetting.settingName === toSetting.settingName &&
							fromSetting.value !== toSetting.value
					)
				)
				.map((difference) => ({
					action: `Updated clinic setting ${formatSettingName(difference.settingName)}`,
					fromValue: difference.value ? difference.value : "null",
					toValue:
						toValue.find(
							(toSetting) => difference.settingName === toSetting.settingName
						)?.value ?? "null"
				}));

			await createAuditLog(
				differences.map((difference) => ({
					clinicId: clinic?.id,
					username: account?.name ?? "",
					applicationScreen: ApplicationScreen.AdminClinicSettings,
					action: difference.action,
					fromValue: difference.fromValue,
					toValue: difference.toValue
				}))
			);
		} catch (error) {
			console.log("Error creating audit log", error);
		}
	};

	const saveClinicSettings = async () => {
		try {
			clearAlert();
			const requestUrl = BuildUrl("Clinic/ClinicSettings");
			const tokenResponse = await instance.acquireTokenSilent({
				account,
				scopes: apiAccessRequest.scopes
			});
			const headers = {
				Authorization: `Bearer ${tokenResponse.accessToken}`,
				"Content-Type": "application/json"
			};

			const response = await fetch(requestUrl, {
				method: "PUT",
				headers,
				body: JSON.stringify(
					updatedSettings(
						omSettings,
						doctorSettings,
						clinicSettings,
						saSettings
					)
				)
			});

			if (!response.ok) {
				throw new Error("Network response was not ok");
			}

			setAlert("Settings updated", AlertType.success);
			await callCreateAuditLog();
			setDataChanged(false);
			originalClinicSettings.current = cloneDeep(clinicSettings);
			originalOmSettings.current = cloneDeep(omSettings);
			originalDoctorSettings.current = cloneDeep(doctorSettings);
			originalSASettings.current = cloneDeep(saSettings);
		} catch (error) {
			console.info(error);
			setAlert("Error updating clinic settings", AlertType.error);
		}
	};

	const handleSaveSettings = async (saveChanges: boolean) => {
		if (!saveChanges) {
			setClinicSettings(cloneDeep(originalClinicSettings.current));
			setOMSettings(cloneDeep(originalOmSettings.current));
			setDoctorSettings(cloneDeep(originalDoctorSettings.current));
			setSASettings(cloneDeep(originalSASettings.current));
			setErrors(undefined);
			setDataChanged(false);
			return;
		}

		const hasChanges =
			!isEqual(clinicSettings, originalClinicSettings.current) ||
			!isEqual(omSettings, originalOmSettings.current) ||
			!isEqual(doctorSettings, originalDoctorSettings.current) ||
			!isEqual(saSettings, originalSASettings.current);

		if (!hasChanges) {
			setAlert(
				"You have not made any changes. No saving done",
				AlertType.error
			);
			setDataChanged(false);
			return;
		}

		await saveClinicSettings();
	};

	const tabContent: Record<
		ClinicSettingsTabKey,
		{ title: string; settings?: ClinicSetting[] }
	> = {
		clinic: { title: "Clinic settings", settings: clinicSettings },
		ourMedicalApp: { title: "Our Medical App settings", settings: omSettings },
		doctorsApp: { title: "Doctors App settings", settings: doctorSettings },
		saApp: { title: "Services Australia settings", settings: saSettings }
	};

	if (isLoading) {
		return (
			<Box className="icon-loading">
				<CircularProgress size={70} />
			</Box>
		);
	}

	const currentSection = tabContent[activeTab];

	return (
		<div className="flex flex-col gap-6">
			<div className="mt-4">
				<FormControl variant="standard" sx={{ m: 1, width: 400 }}>
					<InputLabel id="select-clinic-label">Clinic</InputLabel>
					<Select
						labelId="select-clinic-label"
						id="select-clinic"
						value={clinic ? clinic.id.toString() : ""}
						onChange={handleClinicChange}
						label="Clinic"
					>
						{userClinics?.map((userClinic) => (
							<MenuItem key={`clinic-${userClinic.id}`} value={userClinic.id}>
								{userClinic.name}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</div>
			<ClinicSettingsTabs activeTab={activeTab} onTabChange={setActiveTab} />

			<ClinicSettingsSection
				title={currentSection.title}
				settings={currentSection.settings}
				errors={errors}
				onSettingChange={handleSettingChange}
				onWaitTimeGroupChange={handleWaitTimeGroupChange}
			/>

			{(clinicSettings || omSettings || doctorSettings || saSettings) && (
				<div className="mt-2 flex gap-5">
					<button
						type="button"
						className={`ch-button ch-button-primary ${!dataChanged ? "ch-button-disabled" : ""} `}
						onClick={() => handleSaveSettings(false)}
						disabled={!dataChanged}
					>
						Cancel
					</button>
					<button
						type="button"
						className={`ch-button ch-button-secondary ${!dataChanged ? "ch-button-disabled" : ""} `}
						onClick={() => handleSaveSettings(true)}
						disabled={!dataChanged}
					>
						Save
					</button>
				</div>
			)}
		</div>
	);
};
